import {
  Handshake, Tag, Store, Package, Network, CalendarDays, UtensilsCrossed,
} from "lucide-react";
import { GALLERY } from "../data/site";
import { EnquiryForm } from "../components/EnquiryForm";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../hooks/usePageMeta";

const MODELS = [
  {
    icon: Handshake,
    title: "Dealership & Distribution",
    desc: "B2B partners manage regional order fulfilment and deliveries. Our growth roadmap includes establishing local dealers within a 10–20 km radius, so products stay close to the businesses that need them.",
    testid: "dealership-model-dealer",
  },
  {
    icon: Tag,
    title: "White-Label Partnership",
    desc: "Eligible partners can explore selling our eco-friendly products under their own brand name — a simple way to enter the sustainable products market.",
    testid: "dealership-model-whitelabel",
  },
];

const WHO = [
  { icon: Network, title: "Dealers & Distributors", desc: "Manage regional fulfilment as part of our growing dealer network." },
  { icon: Store, title: "Wholesalers & Retailers", desc: "Stock in-demand eco products for your local market." },
  { icon: CalendarDays, title: "Event Organisers & Caterers", desc: "Reliable eco tableware supply for functions and large gatherings." },
  { icon: UtensilsCrossed, title: "Hotels, Restaurants & Cafes", desc: "Plastic-free plates, bowls, thalis and straws for guest-facing service." },
  { icon: Package, title: "Bulk Buyers", desc: "Purchase directly for events, catering or institutional use." },
  { icon: Tag, title: "White-Label Partners", desc: "Explore selling our products under your own brand name." },
];

export default function Dealership() {
  usePageMeta(
    "Dealer & Distribution Partnership | ORGANIKTHINGS LLP",
    "Partner with ORGANIKTHINGS LLP — dealership, distribution, wholesale and white-label opportunities for eco-friendly leaf plates, bowls, thalis and grass straws across India."
  );
  return (
    <main data-testid="dealership-page">
      <section className="bg-[#f4f1ea] py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-[#2d6a4f] mb-3">
              Dealer & Distribution
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0d1f18] leading-tight">
              Grow Your Business With Organik Things
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#2d3748] leading-relaxed max-w-xl">
              Join us in bringing eco-friendly, biodegradable disposables to
              your region. Our long-term ambition is a district-wise dealer
              network across India — and we'd like you to be part of it.
            </p>
          </div>
          <Reveal>
            <img
              src={GALLERY.thaliSpread}
              alt="Organik Things leaf thalis and bowls ready for supply"
              className="w-full h-72 sm:h-96 object-cover rounded-2xl shadow-lg"
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20" data-testid="dealership-models">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0d1f18]">
              Two partnership models
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {MODELS.map((m, i) => (
              <Reveal key={m.title} delay={i * 80}>
                <div className="bg-[#faf9f6] rounded-xl border border-[#e2ded4] p-7 h-full" data-testid={m.testid}>
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#d8f3dc] text-[#1b4332]">
                    <m.icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-4 font-heading text-xl font-bold text-[#0d1f18]">{m.title}</h3>
                  <p className="mt-3 text-sm sm:text-base text-[#5a6b5c] leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#faf9f6] py-16 md:py-20" data-testid="dealership-who">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0d1f18]">
              Who can partner with us
            </h2>
            <p className="mt-4 text-base text-[#5a6b5c] leading-relaxed max-w-2xl">
              Partners can contact us to discuss their business requirements,
              product availability and distribution opportunities — whether you
              run an established business or are exploring eco-products for the
              first time.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHO.map((w, i) => (
              <Reveal key={w.title} delay={i * 60}>
                <div className="bg-white rounded-xl border border-[#e2ded4] p-6 h-full">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#d8f3dc] text-[#1b4332]">
                    <w.icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-[#0d1f18]">{w.title}</h3>
                  <p className="mt-2 text-sm text-[#5a6b5c] leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20" data-testid="dealership-enquiry">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-[#2d6a4f] mb-3">Partnership Enquiry</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0d1f18]">
              Tell us about your business
            </h2>
            <p className="mt-4 text-base text-[#5a6b5c] leading-relaxed max-w-lg">
              Fill in the form and it opens WhatsApp with your enquiry
              pre-filled — our team will take it forward from there. Dealership
              and distribution terms are discussed individually with each
              partner.
            </p>
          </div>
          <Reveal>
            <EnquiryForm mode="dealer" testidPrefix="dealership" />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
